import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Clipboard, Image, Search, Smartphone, Settings, Shield, LogOut, Menu, X, Download, FileText, CloudOff, Cloud, Loader2, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { usePWAInstall } from '../hooks/usePWAInstall.js';
import { useOnlineStatus } from '../hooks/useOnlineStatus.js';
import { useSyncQueue } from '../hooks/useStorage.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { syncEngine } from '../services/sync.js';
import { cn } from '../lib/utils.js';
import { useState, useEffect, useRef } from 'react';

const NAV = [
  { to: '/app/clipboard', label: 'Clipboard', icon: Clipboard },
  { to: '/app/notes',     label: 'Notes',     icon: FileText },
  { to: '/app/snaps',     label: 'Snaps',     icon: Image },
  { to: '/app/shared',    label: 'Shared',    icon: Send },
  { to: '/app/search',    label: 'Search',    icon: Search },
];

const MORE_NAV = [
  { to: '/app/devices',   label: 'Devices',   icon: Smartphone },
  { to: '/app/settings',  label: 'Settings',  icon: Settings },
];

const navItemCls = ({ isActive }) => cn(
  'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all duration-150',
  isActive
    ? 'bg-accent/15 text-accent-light font-medium'
    : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200',
);

const activeBar = (isActive) =>
  isActive ? 'absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-accent rounded-r-full' : '';

export default function Layout({ children }) {
  const { user, isAdmin, logout } = useAuth();
  const { canInstall, install } = usePWAInstall();
  const { push } = useToast();
  const isOnline = useOnlineStatus();
  const { queue, refresh } = useSyncQueue();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const wasOffline = useRef(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Connection Status Notifications & Re-sync
  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      push({
        kind: 'error',
        message: 'Working offline. Changes will sync when reconnected.',
        title: 'Offline Mode',
        ttl: 5000
      });
    } else if (isOnline && wasOffline.current) {
      wasOffline.current = false;
      push({
        kind: 'success',
        message: 'Connection restored. Syncing your changes…',
        title: 'Back Online',
        ttl: 3000
      });
      syncEngine.sync().then(() => {
        setTimeout(refresh, 1000);
      });
    }
  }, [isOnline, push, refresh]);

  // Close more menu on navigation
  useEffect(() => {
    setIsMoreOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-bg text-zinc-100 pb-20 md:pb-0">
      {/* ─── Top header ─── */}
      <header className="sticky top-0 z-30 border-b border-border/60 bg-panel/70 backdrop-blur">
        <div className={cn('mx-auto flex items-center justify-between px-4 py-3', location.pathname !== '/app/notes' && 'max-w-6xl')}>
          <div className="flex items-center gap-4">
            <Link to="/app/clipboard" className="flex items-center gap-2.5 font-semibold tracking-tight">
              <img src="https://x9ogt2f8d1hnsynj.public.blob.vercel-storage.com/Syncvault/icon.png" alt="SyncVault Logo" className="h-7 w-7 rounded-lg" />
              <span className="bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text text-transparent">SyncVault</span>
            </Link>

            {/* Network Status Indicator */}
            <div className="flex items-center gap-2 border-l border-border/40 pl-4">
              {!isOnline ? (
                <div className="flex items-center gap-1.5 text-rose-400 text-[10px] font-medium uppercase tracking-wider bg-rose-500/10 px-2 py-0.5 rounded-full animate-pulse">
                  <CloudOff size={11} />
                  <span>Offline</span>
                </div>
              ) : queue.length > 0 ? (
                <div className="flex items-center gap-1.5 text-amber-400 text-[10px] font-medium uppercase tracking-wider bg-amber-500/10 px-2 py-0.5 rounded-full">
                  <Loader2 size={11} className="animate-spin" />
                  <span>Syncing {queue.length}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-emerald-400 text-[10px] font-medium uppercase tracking-wider bg-emerald-500/10 px-2 py-0.5 rounded-full">
                  <Cloud size={11} />
                  <span>Synced</span>
                </div>
              )}
            </div>
          </div>

          {/* Right side — desktop shows username chip; mobile just the chip */}
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden text-zinc-500 sm:inline text-xs">@{user?.username}</span>
            <span className="chip">{user?.role}</span>
          </div>
        </div>
      </header>

      <div className={cn('mx-auto flex w-full flex-1 gap-4 px-4 py-6', location.pathname !== '/app/notes' && 'max-w-6xl')}>
        {/* ─── Desktop sidebar ─── */}
        <aside className="hidden w-48 shrink-0 md:flex md:flex-col md:gap-2">
          {/* Main nav card */}
          <nav className="card flex flex-col gap-0.5 p-2 flex-1">
            {/* Primary nav */}
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={navItemCls}>
                {({ isActive }) => (
                  <>
                    <span className={activeBar(isActive)} />
                    <Icon size={15} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}

            {/* Separator */}
            <div className="my-1.5 border-t border-border/50" />

            {/* Secondary nav */}
            {MORE_NAV.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={navItemCls}>
                {({ isActive }) => (
                  <>
                    <span className={activeBar(isActive)} />
                    <Icon size={15} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}

            {isAdmin && (
              <NavLink
                to="/app/admin"
                className={({ isActive }) => cn(
                  'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all duration-150',
                  isActive ? 'bg-amber-500/15 text-amber-300 font-medium' : 'text-amber-200/70 hover:bg-white/5 hover:text-amber-200',
                )}
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-amber-400 rounded-r-full" />}
                    <Shield size={15} />
                    Admin
                  </>
                )}
              </NavLink>
            )}

            {canInstall && (
              <button
                onClick={install}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-accent-light transition-all duration-150 hover:bg-accent/10"
              >
                <Download size={15} /> Install App
              </button>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Logout at bottom */}
            <div className="mt-1 border-t border-border/50 pt-1">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-zinc-500 transition-all duration-150 hover:bg-rose-500/10 hover:text-rose-400"
              >
                <LogOut size={15} /> Logout
              </button>
            </div>
          </nav>
        </aside>

        {/* ─── Page content ─── */}
        <main className="flex-1 min-w-0 animate-fade-in">
          {children}
        </main>
      </div>

      {/* ─── Bottom nav (mobile) ─── */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/60 bg-panel/90 backdrop-blur md:hidden">
        <div className="flex items-center justify-around p-2">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => cn(
                'flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-[10px] transition-all duration-150',
                isActive ? 'text-accent-light' : 'text-zinc-500',
              )}
            >
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setIsMoreOpen(!isMoreOpen)}
            className={cn(
              'flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-[10px] transition-all duration-150',
              isMoreOpen ? 'text-accent-light' : 'text-zinc-500',
            )}
          >
            {isMoreOpen ? <X size={20} /> : <Menu size={20} />}
            <span>More</span>
          </button>
        </div>
      </nav>

      {/* ─── More menu overlay (mobile) ─── */}
      {isMoreOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden" onClick={() => setIsMoreOpen(false)}>
          <div
            className="absolute bottom-20 left-4 right-4 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card flex flex-col gap-1 p-2">
              {MORE_NAV.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-base transition-all duration-150',
                    isActive ? 'bg-accent/15 text-accent-light' : 'text-zinc-300 hover:bg-white/5',
                  )}
                >
                  <Icon size={20} /> {label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to="/app/admin"
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-base transition-all duration-150',
                    isActive ? 'bg-amber-500/15 text-amber-300' : 'text-amber-200/80 hover:bg-white/5',
                  )}
                >
                  <Shield size={20} /> Admin
                </NavLink>
              )}
              {canInstall && (
                <button
                  onClick={install}
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-base text-accent-light transition-all duration-150 hover:bg-accent/10"
                >
                  <Download size={20} /> Install App
                </button>
              )}
              <hr className="my-2 border-border/40" />
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 rounded-lg px-4 py-3 text-base text-rose-400 transition-all duration-150 hover:bg-rose-500/10"
              >
                <LogOut size={20} /> Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
